import api from './axios';

/**
 * Architecture Rule 74 — REST client for the Voice + Video Call subsystem.
 *
 * Pairs with `useCallsQuery.js` (TanStack Query hooks). The functions below
 * are thin axios wrappers; everything (Idempotency-Key, request_id) is
 * already attached by the shared axios instance.
 *
 * Auth tokens for the LiveKit room itself are returned in the START/JOIN
 * response payloads (`data.token = { token, ws_url, room_name, expires_in }`).
 * The SPA never sees LiveKit's API key/secret.
 */

const BASE = '/calls';

/** Start a new call in a thread. Returns { call, token }. */
export const startCallRequest = (threadId, kind = 'voice') =>
  api.post(BASE, { thread_id: threadId, kind }, {
    headers: {
      'Idempotency-Key':
        (typeof window !== 'undefined' && window.crypto?.randomUUID?.()) ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    },
  });

/** Fetch full state for one call (status, participants, recording). */
export const getCallRequest = (callId) => api.get(`${BASE}/${callId}`);

/** Accept an incoming call. Returns { call, token } with a fresh JWT. */
export const joinCallRequest = (callId) => api.post(`${BASE}/${callId}/join`);

/** Decline an incoming call (1:1 only). */
export const declineCallRequest = (callId) => api.post(`${BASE}/${callId}/decline`);

/** Hangup for the current user. Last one out ends the call. */
export const leaveCallRequest = (callId) => api.post(`${BASE}/${callId}/leave`);

/** Force-end the call for everyone (any participant can end). */
export const endCallRequest = (callId) => api.post(`${BASE}/${callId}/end`);

/** Get the permanent public share URL for a completed call's recording. */
export const getCallRecordingUrlRequest = (callId) =>
  api.get(`${BASE}/${callId}/recording/download`);

/** Paginated call history for one thread (newest first). */
export const listCallsForThreadRequest = (threadId, params = {}) =>
  api.get(`/threads/${threadId}/calls`, { params });
